import * as React from "react";
import { Plus, DownloadSimple, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import type {
  InvoiceListViewProps,
  InvoiceListFilters,
  InvoiceSortField,
  InvoiceRow,
  InvoiceStatus,
} from "./types";
import { InvoiceSummaryCards } from "./InvoiceSummaryCards";
import { InvoiceFilterBar } from "./InvoiceFilterBar";
import { InvoiceTabChips } from "./InvoiceTabChips";
import { InvoiceDataGrid } from "./InvoiceDataGrid";
import { InvoiceDetailDrawer } from "./InvoiceDetailDrawer";
import { InvoiceSendDialog } from "./InvoiceSendDialog";
import { InvoiceVoidDialog } from "./InvoiceVoidDialog";
import { InvoiceBulkActionBar } from "./InvoiceBulkActionBar";
import { isOverdue } from "./utils";

export function InvoiceListView(props: InvoiceListViewProps) {
  const {
    snapshot,
    labels,
    locale,
    isLoading,
    error,
    selectedIds: extSelected,
    onFiltersChange,
    onSelectionChange: extOnSelection,
    onRowClick,
    onIssue,
    onSend,
    onVoid,
    onDownload,
    onCopyPayLink,
    onBulkAction,
    onCreateManual,
    onPageChange,
    onRetry,
    dataTestId,
  } = props;

  // Local filter state if uncontrolled
  const [localFilters, setLocalFilters] = React.useState<InvoiceListFilters>(snapshot.filters);
  React.useEffect(() => setLocalFilters(snapshot.filters), [snapshot.filters]);
  const [activeTab, setActiveTab] = React.useState<InvoiceStatus | "all" | "overdue">("all");

  const updateFilters = (next: InvoiceListFilters) => {
    setLocalFilters(next);
    onFiltersChange?.(next);
  };

  // Selection
  const [internalSel, setInternalSel] = React.useState<readonly string[]>([]);
  const selectedIds = extSelected ?? internalSel;
  const setSelected = (ids: readonly string[]) => {
    setInternalSel(ids);
    extOnSelection?.(ids);
  };

  // Detail drawer
  const [openId, setOpenId] = React.useState<string | null>(null);
  const openRow = React.useMemo(
    () => snapshot.rows.find((r) => r.id === openId) ?? null,
    [openId, snapshot.rows],
  );
  const handleRowClick = (id: string) => {
    setOpenId(id);
    onRowClick?.(id);
  };

  // Send / Void dialogs
  const [sendCtx, setSendCtx] = React.useState<{ row: InvoiceRow; channel: "email" | "whatsapp" } | null>(null);
  const [voidCtx, setVoidCtx] = React.useState<InvoiceRow | null>(null);

  // Sort handler
  const onSort = (field: InvoiceSortField) => {
    const dir =
      localFilters.sort.field === field
        ? localFilters.sort.dir === "asc"
          ? "desc"
          : "asc"
        : "desc";
    updateFilters({ ...localFilters, sort: { field, dir } });
  };

  // Local filter + tab application (presentational; host can ignore via onFiltersChange)
  const filteredRows = React.useMemo(() => {
    const today = new Date();
    let rows = [...snapshot.rows];

    // tab
    if (activeTab === "overdue") {
      rows = rows.filter((r) => isOverdue(r, today));
    } else if (activeTab !== "all") {
      rows = rows.filter((r) => r.status === activeTab);
    }

    // status filter (multi)
    if (localFilters.status.length > 0) {
      rows = rows.filter((r) => localFilters.status.includes(r.status));
    }
    if (localFilters.type.length > 0) {
      rows = rows.filter((r) => localFilters.type.includes(r.type));
    }
    if (localFilters.dateRange) {
      rows = rows.filter((r) => {
        if (!r.issuedAt) return false;
        const d = r.issuedAt.slice(0, 10);
        return d >= localFilters.dateRange!.from && d <= localFilters.dateRange!.to;
      });
    }
    if (localFilters.search.trim()) {
      const q = localFilters.search.toLowerCase();
      rows = rows.filter(
        (r) =>
          (r.invoiceNumber ?? "").toLowerCase().includes(q) ||
          r.customer.name.toLowerCase().includes(q) ||
          (r.customer.taxNumber ?? "").includes(q),
      );
    }

    // sort
    const { field, dir } = localFilters.sort;
    rows.sort((a, b) => {
      const mult = dir === "asc" ? 1 : -1;
      switch (field) {
        case "totalMinor":
          return (a.totalMinor - b.totalMinor) * mult;
        case "issuedAt":
          return ((a.issuedAt ?? "") < (b.issuedAt ?? "") ? -1 : 1) * mult;
        case "customerName":
          return a.customer.name.localeCompare(b.customer.name) * mult;
        case "invoiceNumber":
          return ((a.invoiceNumber ?? "") < (b.invoiceNumber ?? "") ? -1 : 1) * mult;
        case "dueAt":
          return ((a.dueAt ?? "") < (b.dueAt ?? "") ? -1 : 1) * mult;
      }
      return 0;
    });

    return rows;
  }, [snapshot.rows, activeTab, localFilters]);

  // Keyboard: `/` focuses search
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
        const el = document.querySelector<HTMLInputElement>(
          `[data-test-id="${dataTestId ?? "invoice-list"}"] input[type="search"]`,
        );
        if (el) {
          e.preventDefault();
          el.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dataTestId]);

  const copyLink = (id: string) => {
    const row = snapshot.rows.find((r) => r.id === id);
    if (row?.shareUrl && navigator.clipboard) {
      navigator.clipboard.writeText(row.shareUrl);
    }
    onCopyPayLink?.(id);
    toast.success(labels.toasts.linkCopied);
  };

  return (
    <div
      data-test-id={dataTestId ?? "invoice-list"}
      className="h-full flex flex-col bg-biz-bg overflow-hidden"
    >
      {/* Header */}
      <header className="flex items-center justify-between px-5 py-4 border-b border-biz-border bg-biz-surface-elevated">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="font-display text-xl font-semibold text-biz-foreground truncate">
              {labels.page.title}
            </h1>
            <span className="text-sm text-biz-foreground-muted hidden sm:inline">·</span>
            <span className="text-sm text-biz-foreground-muted hidden sm:inline truncate">
              {snapshot.branchName}
            </span>
          </div>
          <p className="text-xs text-biz-foreground-muted mt-0.5">{labels.page.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="h-10 px-3 inline-flex items-center gap-1.5 rounded-biz-btn border border-biz-border text-sm font-semibold hover:bg-biz-muted biz-focus-ring"
          >
            <DownloadSimple size={14} />
            <span className="hidden sm:inline">{labels.page.exportAction}</span>
          </button>
          <button
            type="button"
            onClick={onCreateManual}
            className="h-10 px-3 inline-flex items-center gap-1.5 rounded-biz-btn bg-biz-primary text-biz-primary-foreground text-sm font-semibold hover:bg-biz-primary-hover biz-focus-ring"
          >
            <Plus size={14} weight="bold" />
            <span className="hidden sm:inline">{labels.page.createAction}</span>
          </button>
        </div>
      </header>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto biz-scrollbar">
        <div className="p-5 space-y-4">
          <InvoiceSummaryCards totals={snapshot.totals} labels={labels} locale={locale} />
          <InvoiceFilterBar filters={localFilters} labels={labels} onChange={updateFilters} />
          <InvoiceTabChips
            snapshot={snapshot}
            labels={labels}
            activeStatus={activeTab}
            onChange={setActiveTab}
          />

          {error ? (
            <ErrorState message={error.message} retryLabel={labels.errors.retry} onRetry={onRetry} />
          ) : isLoading ? (
            <LoadingState />
          ) : filteredRows.length === 0 ? (
            <EmptyState
              title={labels.empty.title}
              description={labels.empty.description}
              ctaLabel={labels.empty.createAction}
              onCta={onCreateManual}
            />
          ) : (
            <InvoiceDataGrid
              snapshot={snapshot}
              labels={labels}
              locale={locale}
              selectedIds={selectedIds}
              filteredRows={filteredRows}
              onSelectionChange={setSelected}
              sort={localFilters.sort}
              onSort={onSort}
              onRowClick={handleRowClick}
              onIssue={(id) => onIssue?.(id)}
              onSend={(row, channel) => setSendCtx({ row, channel })}
              onVoid={(id) => {
                const r = snapshot.rows.find((x) => x.id === id);
                if (r) setVoidCtx(r);
              }}
              onDownload={(id, format) => onDownload?.(id, format)}
              onCopyPayLink={copyLink}
            />
          )}

          {/* Pagination */}
          {!isLoading && !error && filteredRows.length > 0 && (
            <div className="flex items-center justify-between text-xs text-biz-foreground-muted">
              <span>
                {filteredRows.length} {labels.pagination.records}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onPageChange?.(Math.max(1, localFilters.page - 1))}
                  disabled={localFilters.page <= 1}
                  className="h-8 px-2 rounded-biz-btn border border-biz-border hover:bg-biz-muted biz-focus-ring disabled:opacity-40"
                >
                  {labels.pagination.prev}
                </button>
                <span className="font-mono-num">
                  {labels.pagination.pageOf} {localFilters.page}
                </span>
                <button
                  type="button"
                  onClick={() => onPageChange?.(localFilters.page + 1)}
                  disabled={!snapshot.hasMore}
                  className="h-8 px-2 rounded-biz-btn border border-biz-border hover:bg-biz-muted biz-focus-ring disabled:opacity-40"
                >
                  {labels.pagination.next}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Drawer */}
      <InvoiceDetailDrawer
        open={openId !== null}
        row={openRow}
        labels={labels}
        locale={locale}
        onClose={() => setOpenId(null)}
        onSend={(row) => setSendCtx({ row, channel: row.customer.email ? "email" : "whatsapp" })}
        onVoid={(row) => setVoidCtx(row)}
        onDownload={(row, format) => onDownload?.(row.id, format)}
        onCopyPayLink={(row) => copyLink(row.id)}
        onRetry={(row) => onIssue?.(row.id)}
      />

      {/* Send Dialog */}
      <InvoiceSendDialog
        open={sendCtx !== null}
        row={sendCtx?.row ?? null}
        labels={labels}
        defaultChannel={sendCtx?.channel ?? "email"}
        onClose={() => setSendCtx(null)}
        onSend={async (channel, target, note) => {
          if (sendCtx) {
            await onSend?.(sendCtx.row.id, channel, target, note);
            toast.success(labels.toasts.sent);
          }
        }}
      />

      {/* Void Dialog */}
      <InvoiceVoidDialog
        open={voidCtx !== null}
        row={voidCtx}
        labels={labels}
        onClose={() => setVoidCtx(null)}
        onConfirm={async (reason, explanation) => {
          if (voidCtx) {
            await onVoid?.(voidCtx.id, reason, explanation);
            toast.success(labels.toasts.voided);
          }
        }}
      />

      {/* Bulk action bar */}
      <InvoiceBulkActionBar
        count={selectedIds.length}
        labels={labels}
        onSend={async () => {
          await onBulkAction?.("send", selectedIds);
          toast.success(`${selectedIds.length} · ${labels.toasts.sent}`);
        }}
        onDownload={async () => {
          await onBulkAction?.("download", selectedIds);
          toast.success(labels.toasts.downloaded);
        }}
        onVoid={async () => {
          await onBulkAction?.("void", selectedIds);
          toast.success(labels.toasts.voided);
          setSelected([]);
        }}
        onClear={() => setSelected([])}
      />
    </div>
  );
}

function LoadingState() {
  return (
    <div className="bg-biz-surface-elevated border border-biz-border rounded-[10px] biz-shadow-sm p-6 space-y-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-12 bg-biz-muted/60 rounded animate-pulse" />
      ))}
    </div>
  );
}

function EmptyState({
  title,
  description,
  ctaLabel,
  onCta,
}: {
  title: string;
  description: string;
  ctaLabel: string;
  onCta?: () => void;
}) {
  return (
    <div className="bg-biz-surface-elevated border border-biz-border rounded-[10px] biz-shadow-sm p-12 text-center">
      <h3 className="font-display text-lg font-semibold text-biz-foreground">{title}</h3>
      <p className="text-sm text-biz-foreground-muted mt-1.5 max-w-md mx-auto">{description}</p>
      {onCta && (
        <button
          type="button"
          onClick={onCta}
          className="mt-4 h-10 px-4 inline-flex items-center gap-1.5 rounded-biz-btn bg-biz-primary text-biz-primary-foreground text-sm font-semibold hover:bg-biz-primary-hover biz-focus-ring"
        >
          <Plus size={14} weight="bold" /> {ctaLabel}
        </button>
      )}
    </div>
  );
}

function ErrorState({
  message,
  retryLabel,
  onRetry,
}: {
  message: string;
  retryLabel: string;
  onRetry?: () => void;
}) {
  return (
    <div className="bg-biz-danger-subtle border border-biz-danger-border rounded-[10px] p-6 text-center">
      <h3 className="font-semibold text-biz-danger">{message}</h3>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 h-10 px-4 inline-flex items-center gap-1.5 rounded-biz-btn bg-biz-danger text-biz-danger-foreground text-sm font-semibold hover:opacity-90 biz-focus-ring"
        >
          <ArrowClockwise size={14} /> {retryLabel}
        </button>
      )}
    </div>
  );
}
